import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { createGame, getGame, triggerRun, updateGame } from '../services/games';
import { isGamewithDomain, normalizeDomain } from '../utils/domain';
import {
  ALL_LANGUAGES,
  INPUT_METHODS_PER_LANGUAGE,
  INPUT_METHOD_LABELS,
  INTERVAL_OPTIONS,
  LANGUAGE_LABELS,
  MAX_CUSTOM_KEYWORDS,
  type GameSettings,
  type InputMethod,
  type SupportedLanguage,
} from '../types';

interface Props {
  mode: 'create' | 'edit';
}

const DEFAULT_FORM = {
  title: '',
  languages: ['en'] as SupportedLanguage[],
  maxKeywords: 50,
  intervalDays: 1,
  enabled: true,
};

/** Parse a comma-separated field into trimmed, non-empty entries. Accepts
 *  the full-width "，" and Japanese "、" alongside the ASCII comma — JP
 *  operators naturally type those, and treating the whole line as one entry
 *  would send a nonsense query to Serper. */
function parseCommaList(text: string | undefined): string[] {
  return (text ?? '')
    .split(/[,，、]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/** Firestore string[] map → editable comma-joined text map. */
function toCommaTextMap(
  m: Partial<Record<SupportedLanguage, string[]>> | undefined
): Partial<Record<SupportedLanguage, string>> {
  return Object.fromEntries(
    Object.entries(m ?? {}).map(([k, list]) => [k, (list ?? []).join(', ')])
  ) as Partial<Record<SupportedLanguage, string>>;
}

/** One text input per enabled language, with a persistent language label
 *  (not a placeholder — it must stay visible after the user types). */
function LangInputGrid({
  languages,
  placeholder,
  values,
  onChange,
}: {
  languages: SupportedLanguage[];
  placeholder: string;
  values: Partial<Record<SupportedLanguage, string>>;
  onChange: (lang: SupportedLanguage, value: string) => void;
}) {
  return (
    <div className="lang-input-grid">
      {languages.map((lang) => (
        <label key={lang} className="lang-input-row">
          <span className="lang-input-label">{LANGUAGE_LABELS[lang]}</span>
          <input
            type="text"
            placeholder={placeholder}
            value={values[lang] ?? ''}
            onChange={(e) => onChange(lang, e.target.value)}
          />
        </label>
      ))}
    </div>
  );
}

export function GameFormPage({ mode }: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const { gameId } = useParams<{ gameId: string }>();
  const [form, setForm] = useState(DEFAULT_FORM);
  const [titleByLanguage, setTitleByLanguage] = useState<Partial<Record<SupportedLanguage, string>>>({});
  // Aliases / custom keywords are edited as RAW comma-separated text per
  // language and parsed only on save. Parsing on each keystroke re-serialized
  // the controlled value (join(', ')) and fought the caret — e.g. a trailing
  // comma was undeletable because backspacing its space got re-expanded.
  const [aliasesTextByLanguage, setAliasesTextByLanguage] = useState<Partial<Record<SupportedLanguage, string>>>({});
  // User-specified rank-tracking keywords. These skip AI filtering/scoring on
  // the functions side (translation runs same as the auto set).
  const [customKeywordsTextByLanguage, setCustomKeywordsTextByLanguage] = useState<Partial<Record<SupportedLanguage, string>>>({});
  // Per-language input-method selection. Defaults are filled in when a
  // language is first ticked so the user always has at least one method.
  const [inputMethodsByLanguage, setInputMethodsByLanguage] = useState<Partial<Record<SupportedLanguage, InputMethod[]>>>({});
  // Raw comma-separated text, parsed on save (same caret reason as aliases).
  const [competitorDomainsText, setCompetitorDomainsText] = useState('');
  const [loading, setLoading] = useState(mode === 'edit');
  const [saving, setSaving] = useState(false);

  // Browser-style "back" — return to wherever the user came from inside the app.
  // When the form was opened directly via URL (no in-app history), location.key
  // is "default"; in that case fall back to the game list so the user isn't
  // stranded or sent to an external referrer.
  const goBack = () => {
    if (location.key !== 'default') navigate(-1);
    else navigate('/');
  };

  useEffect(() => {
    if (mode !== 'edit' || !gameId) return;
    let active = true;
    getGame(gameId).then((g) => {
      if (!active || !g) return;
      setForm({
        title: g.title,
        languages: g.languages,
        maxKeywords: g.maxKeywords,
        intervalDays: g.intervalDays ?? 1,
        enabled: g.enabled,
      });
      setTitleByLanguage(g.titleByLanguage ?? {});
      setAliasesTextByLanguage(toCommaTextMap(g.aliasesByLanguage));
      setInputMethodsByLanguage(g.inputMethodsByLanguage ?? {});
      setCustomKeywordsTextByLanguage(toCommaTextMap(g.customKeywordsByLanguage));
      setCompetitorDomainsText((g.competitorDomains ?? []).join(', '));
      setLoading(false);
    });
    return () => {
      active = false;
    };
  }, [mode, gameId]);

  const toggleLanguage = (lang: SupportedLanguage) => {
    setForm((f) => {
      const next = f.languages.includes(lang) ? f.languages.filter((l) => l !== lang) : [...f.languages, lang];
      return { ...f, languages: next };
    });
    // Seed the input-method selection with this language's defaults the first
    // time it's enabled, so the user always has at least one method picked.
    setInputMethodsByLanguage((m) => {
      if (m[lang]?.length) return m;
      return { ...m, [lang]: [...INPUT_METHODS_PER_LANGUAGE[lang].defaults] };
    });
  };

  /**
   * Toggle a single input method for a language. Refuses to remove the last
   * remaining method — the UI also locks the checkbox in that state, but we
   * guard here too in case the disabled attribute gets bypassed.
   */
  const toggleInputMethod = (lang: SupportedLanguage, method: InputMethod) => {
    setInputMethodsByLanguage((m) => {
      const current = m[lang] ?? INPUT_METHODS_PER_LANGUAGE[lang].defaults;
      if (current.includes(method)) {
        if (current.length === 1) return m; // last one, can't unset
        return { ...m, [lang]: current.filter((x) => x !== method) };
      }
      // Preserve the canonical order from the per-language `available` list.
      const available = INPUT_METHODS_PER_LANGUAGE[lang].available;
      const next = available.filter((x) => current.includes(x) || x === method);
      return { ...m, [lang]: next };
    });
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim()) {
      alert('タイトルを入力してください。');
      return;
    }
    if (form.languages.length === 0) {
      alert('言語を1つ以上選択してください。');
      return;
    }
    // Block the save instead of silently truncating at the cap — a silent
    // slice would permanently drop the excess entries (edit mode reloads the
    // saved doc, so the user could never get them back). Only the selected
    // languages are checked: an unticked language's field isn't rendered, so
    // blocking on it would lock the form with nothing visible to fix.
    const overCap = form.languages
      .filter((lang) => parseCommaList(customKeywordsTextByLanguage[lang]).length > MAX_CUSTOM_KEYWORDS)
      .map((lang) => LANGUAGE_LABELS[lang]);
    if (overCap.length > 0) {
      alert(`指定ワードは各言語 最大${MAX_CUSTOM_KEYWORDS}件までです。超過している言語: ${overCap.join(', ')}`);
      return;
    }
    // Normalize to bare hosts so the saved list matches how functions match
    // result URLs; block on anything unparseable rather than dropping it.
    const competitorDomains: string[] = [];
    const invalidDomains: string[] = [];
    const gamewithDomains: string[] = [];
    for (const raw of parseCommaList(competitorDomainsText)) {
      const d = normalizeDomain(raw);
      if (!d) invalidDomains.push(raw);
      else if (isGamewithDomain(d)) gamewithDomains.push(raw);
      else if (!competitorDomains.includes(d)) competitorDomains.push(d);
    }
    if (invalidDomains.length > 0) {
      alert(`他社ドメインの形式が正しくありません: ${invalidDomains.join(', ')}\n例: game8.jp`);
      return;
    }
    if (gamewithDomains.length > 0) {
      alert(`gamewith のドメインは常に計測されるため、他社ドメインには指定できません: ${gamewithDomains.join(', ')}`);
      return;
    }
    setSaving(true);
    try {
      const cleanedLocaleTitles = Object.fromEntries(
        Object.entries(titleByLanguage).filter(([, v]) => (v ?? '').trim() !== '')
      ) as Partial<Record<SupportedLanguage, string>>;
      // Drop empty alias arrays so we don't persist meaningless empties.
      const cleanedAliases = Object.fromEntries(
        Object.entries(aliasesTextByLanguage)
          .map(([k, text]) => [k, parseCommaList(text)])
          .filter(([, list]) => (list as string[]).length > 0)
      ) as Partial<Record<SupportedLanguage, string[]>>;
      // Persist only the languages the user actually selected. Defaults are
      // also written explicitly so saved docs always reflect current choices.
      const cleanedInputMethods = Object.fromEntries(
        form.languages
          .map((lang) => [lang, inputMethodsByLanguage[lang] ?? INPUT_METHODS_PER_LANGUAGE[lang].defaults])
          .filter(([, list]) => (list as InputMethod[]).length > 0)
      ) as Partial<Record<SupportedLanguage, InputMethod[]>>;
      // Same cleaning as aliases. The slice is a quiet backstop for hidden
      // fields only: unticked languages keep their saved text but aren't
      // covered by the over-cap block above (their inputs aren't rendered).
      // Visible languages never reach it over the cap.
      const cleanedCustomKeywords = Object.fromEntries(
        Object.entries(customKeywordsTextByLanguage)
          .map(([k, text]) => [k, parseCommaList(text).slice(0, MAX_CUSTOM_KEYWORDS)])
          .filter(([, list]) => (list as string[]).length > 0)
      ) as Partial<Record<SupportedLanguage, string[]>>;
      const payload: Omit<GameSettings, 'id' | 'createdAt' | 'updatedAt' | 'lastRunAt'> = {
        title: form.title.trim(),
        languages: form.languages,
        maxKeywords: Math.max(1, Math.min(100, Number(form.maxKeywords))),
        intervalDays: Math.max(1, Math.min(365, Number(form.intervalDays))),
        enabled: form.enabled,
        titleByLanguage: cleanedLocaleTitles,
        aliasesByLanguage: cleanedAliases,
        inputMethodsByLanguage: cleanedInputMethods,
        customKeywordsByLanguage: cleanedCustomKeywords,
        competitorDomains,
      };
      if (mode === 'create') {
        const id = await createGame(payload);
        // Fire-and-forget like the other "今すぐ実行" call sites: the pipeline
        // is long-running, errors land in Cloud Functions logs.
        triggerRun(id).catch((err) => {
          console.error('triggerRun failed', err);
        });
        alert('ゲームを作成し、初回データ取得を開始しました。完了後、ページを再読み込みすると最新の結果が表示されます。');
        navigate(`/games/${id}`);
      } else if (gameId) {
        await updateGame(gameId, payload);
        navigate(`/games/${gameId}`);
      }
    } catch (err) {
      alert(`保存に失敗しました: ${(err as Error).message}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div>読み込み中…</div>;

  return (
    <>
      <div className="row" style={{ justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>{mode === 'create' ? 'ゲームを追加' : 'ゲーム設定'}</h2>
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            goBack();
          }}
        >
          ← 戻る
        </a>
      </div>

      <form onSubmit={onSubmit} className="card">
        <div className="form-row">
          <label>ゲームタイトル</label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            placeholder="例: pokemon champions"
            required
          />
        </div>

        <div className="form-row">
          <label>対応言語（複数選択可）</label>
          <div className="lang-list">
            {ALL_LANGUAGES.map((lang) => {
              const enabled = form.languages.includes(lang);
              const config = INPUT_METHODS_PER_LANGUAGE[lang];
              const methods = inputMethodsByLanguage[lang] ?? config.defaults;
              return (
                <div key={lang} className="lang-row">
                  <label className="lang-row-label">
                    <input
                      type="checkbox"
                      checked={enabled}
                      onChange={() => toggleLanguage(lang)}
                    />
                    {LANGUAGE_LABELS[lang]}
                  </label>
                  {enabled && (
                    <div className="lang-row-methods">
                      <span className="muted lang-row-methods-label">キーワード取得方法</span>
                      {config.available.map((method) => {
                        const selected = methods.includes(method);
                        // Lock the checkbox when it's the only available option
                        // for the language, or when it's the last remaining
                        // selection (preventing 0-method state).
                        const onlyChoice = config.available.length === 1;
                        const lastRemaining = selected && methods.length === 1;
                        const locked = onlyChoice || lastRemaining;
                        return (
                          <label key={method} className={locked ? 'method-locked' : undefined}>
                            <input
                              type="checkbox"
                              checked={selected}
                              disabled={locked && selected}
                              onChange={() => toggleInputMethod(lang, method)}
                            />
                            {INPUT_METHOD_LABELS[method]}
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {form.languages.length > 0 && (
          <div className="form-row">
            <label>言語別タイトル（任意・指定があれば優先される）</label>
            <LangInputGrid
              languages={form.languages}
              placeholder="タイトル"
              values={titleByLanguage}
              onChange={(lang, v) => setTitleByLanguage((m) => ({ ...m, [lang]: v }))}
            />
          </div>
        )}

        {form.languages.length > 0 && (
          <div className="form-row">
            <label>略称（任意・カンマ区切りで複数指定可）</label>
            <span className="muted" style={{ fontSize: 12, marginBottom: 4 }}>
              例: ポケチャン, ポケモンCh — 各略称ごとに a–z サジェスト取得 + AIフィルタを別途実行します
            </span>
            <LangInputGrid
              languages={form.languages}
              placeholder="略称（カンマ区切り）"
              values={aliasesTextByLanguage}
              onChange={(lang, v) => setAliasesTextByLanguage((m) => ({ ...m, [lang]: v }))}
            />
          </div>
        )}

        {form.languages.length > 0 && (
          <div className="form-row">
            <label>指定ワード（任意・カンマ区切りで複数指定可・各言語 最大{MAX_CUSTOM_KEYWORDS}件）</label>
            <span className="muted" style={{ fontSize: 12, marginBottom: 4 }}>
              例: 攻略, tier list — ゲーム名は入力不要です。実行時は「ゲームタイトル + 指定ワード」で Serper 検索して順位を確認します（AIフィルタ・スコアリングなし）。結果は自動選定キーワードとは別枠で表示されます
            </span>
            <LangInputGrid
              languages={form.languages}
              placeholder="指定ワード（カンマ区切り）"
              values={customKeywordsTextByLanguage}
              onChange={(lang, v) => setCustomKeywordsTextByLanguage((m) => ({ ...m, [lang]: v }))}
            />
          </div>
        )}

        <div className="form-row">
          <label>他社ドメイン（任意・カンマ区切りで複数指定可）</label>
          <span className="muted" style={{ fontSize: 12, marginBottom: 4 }}>
            例: game8.jp, altema.jp — 全言語のキーワードで gamewith と並べて順位を表示します。サブドメインも同じドメインとして数えます。追加した日から順位履歴に記録されます（最新の実行結果には追加直後から表示）
          </span>
          <input
            type="text"
            placeholder="他社ドメイン（カンマ区切り）"
            value={competitorDomainsText}
            onChange={(e) => setCompetitorDomainsText(e.target.value)}
          />
        </div>

        <div className="form-row">
          <label>取得する最大キーワード数（1〜100）</label>
          <input
            type="number"
            min={1}
            max={100}
            value={form.maxKeywords}
            onChange={(e) => setForm((f) => ({ ...f, maxKeywords: Number(e.target.value) }))}
          />
        </div>

        <div className="form-row">
          <label>更新頻度</label>
          <select
            value={form.intervalDays}
            onChange={(e) => setForm((f) => ({ ...f, intervalDays: Number(e.target.value) }))}
          >
            {INTERVAL_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <span className="muted">※ 実行時刻は 09:00 JST 固定です。</span>
        </div>

        <div className="form-row">
          <label>
            <input
              type="checkbox"
              checked={form.enabled}
              onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
            />
            {' '}有効化（無効にすると定期実行から外れます）
          </label>
        </div>

        <div className="row">
          <button className="primary" type="submit" disabled={saving}>
            {saving ? '保存中…' : '保存'}
          </button>
          <button className="secondary" type="button" onClick={goBack}>
            キャンセル
          </button>
        </div>
      </form>
    </>
  );
}
